// BarcodeScanner.js

import React, { useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';

function BarcodeScanner({ onDetected, active = true, deviceId }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const zxingReaderRef = useRef(null);
  const zxingControlsRef = useRef(null);

  const lastTapAtRef = useRef(0);
  const lastCodeRef = useRef('');
  const lastCodeAtRef = useRef(0);
  const codeHitsRef = useRef(0);
  const onDetectedRef = useRef(onDetected);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    let cancelled = false;
    lastCodeRef.current = '';
    lastCodeAtRef.current = 0;
    codeHitsRef.current = 0;
    // Re-start the camera stream whenever deviceId changes

    const handleCode = (code, opts = {}) => {
      if (!code) return;

      const requireMultipleHits = opts.requireMultipleHits !== false;

      const now = Date.now();
      const lastCode = lastCodeRef.current;
      const lastAt = lastCodeAtRef.current;

      // If we're not requiring multiple hits (ZXing path), fire immediately
      // but suppress duplicates for a short cooldown window.
      if (!requireMultipleHits) {
        if (code === lastCode && (now - lastAt) < 1500) return;
        lastCodeRef.current = code;
        lastCodeAtRef.current = now;
        codeHitsRef.current = 0;
        if (typeof onDetectedRef.current === 'function') {
          onDetectedRef.current(code);
        }
        return;
      }

      if (code === lastCode && (now - lastAt) < 900) {
        codeHitsRef.current += 1;
      } else {
        lastCodeRef.current = code;
        codeHitsRef.current = 1;
      }
      lastCodeAtRef.current = now;

      if (codeHitsRef.current >= 2) {
        codeHitsRef.current = 0;
        lastCodeRef.current = '';
        if (typeof onDetectedRef.current === 'function') {
          onDetectedRef.current(code);
        }
      }
    };

    const start = async () => {
      if (!active) return;
      if (!videoRef.current) return;

      // If a specific deviceId is selected use it; otherwise default to rear camera.
      const videoConstraint = deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
        : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: 'continuous' }] };

      const constraints = { video: videoConstraint, audio: false };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        // Safari/iOS can reject unsupported constraint keys (e.g. focusMode).
        stream = await navigator.mediaDevices.getUserMedia({
          video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } },
          audio: false
        });
      }
      if (cancelled) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      const supportsBarcodeDetector = typeof window !== 'undefined' && typeof window.BarcodeDetector !== 'undefined';

      if (supportsBarcodeDetector) {
        const formats = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'];
        const detector = new window.BarcodeDetector({ formats });

        const tick = async () => {
          if (cancelled) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (Array.isArray(barcodes) && barcodes.length > 0) {
              const rawValue = barcodes[0]?.rawValue;
              handleCode(rawValue, { requireMultipleHits: true });
            }
          } catch (e) {
            // no-op
          }
          rafRef.current = window.requestAnimationFrame(tick);
        };

        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }

      if (!zxingReaderRef.current) {
        zxingReaderRef.current = new BrowserMultiFormatReader();
      }

      try {
        // Safari/iOS: prefer continuous decode callback instead of a single awaited decode.
        // decodeFromVideoDevice will keep scanning until controls.stop() is called.
        zxingControlsRef.current = await zxingReaderRef.current.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result) => {
            if (!result) return;
            handleCode(result?.getText?.() || result?.text, { requireMultipleHits: false });
          }
        );
      } catch (e) {
        // no-op
      }
    };

    start().catch(() => {
      // no-op
    });

    return () => {
      cancelled = true;
      if (rafRef.current) {
        try {
          window.cancelAnimationFrame(rafRef.current);
        } catch (e) {
          // no-op
        }
      }

      try {
        zxingControlsRef.current?.stop?.();
      } catch (e) {
        // no-op
      }
      zxingControlsRef.current = null;

      try {
        zxingReaderRef.current?.reset?.();
      } catch (e) {
        // no-op
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [active, deviceId]);

  const handleTapToFocus = async () => {
    const now = Date.now();
    if (now - lastTapAtRef.current < 250) return;
    lastTapAtRef.current = now;

    try {
      const track = streamRef.current?.getVideoTracks?.()?.[0];
      if (!track || typeof track.applyConstraints !== 'function') return;

      await track.applyConstraints({
        advanced: [{ focusMode: 'continuous' }]
      });
    } catch (e) {
      // no-op
    }
  };

  return (
    <video
      ref={videoRef}
      onClick={handleTapToFocus}
      onTouchStart={handleTapToFocus}
      playsInline
      muted
      style={{ width: '100%', minHeight: 320, objectFit: 'cover' }}
    />
  );
}

export default BarcodeScanner;
